import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, filter, first, map, Observable, switchMap, tap } from 'rxjs';
import { ProductTypeEnum, UserLastSignIn, UserProfile, UserRepository } from '../core';
import { BaseService } from './base.service';


/**
 * This service handels user profile.
 */
@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {

	private readonly userRepo = inject(UserRepository);

	private readonly _currentUser$ = new BehaviorSubject<UserProfile | null>(null);

	private readonly CUSTOMER_CARE: string = 'CustomerCare';


	/**
	 * Get first name and last name of user profile.
	 */
	getUserName(): Observable<{ firstName: string; lastName: string }> {
		return this.getCurrentUser().pipe(map(user => ({ firstName: user?.firstName, lastName: user?.lastName })));
	}

	/**
	 * Get language of user profile.
	 */
	getUserLanguage(): Observable<string | undefined> {
		return this.getCurrentUser().pipe(map(user => user.language));
	}

	/**
	 * Get current user profile.
	 */
	getCurrentUser(): Observable<UserProfile> {
		return this._currentUser$.asObservable().pipe(first((user): user is UserProfile => !!user));
	}

	/**
	 * Load user profile.
	 */
	loadUser(): Observable<UserProfile> {
		return this._getUserProfile().pipe(
			filter((user): user is UserProfile => !!user),
			tap(user => this._currentUser$.next(user))
		);
	}

	/**
	* Get user guid by user name.
	*/
	getUserGuid(username: string): Observable<string | undefined> {
		return this.userRepo.getUserGuid(username).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	* Check if user is customer care.
	*/
	isCustomerCare(): Observable<boolean> {
		return this.getCurrentUser().pipe(
			map(usr => usr.roles.includes(this.CUSTOMER_CARE))
		);
	}

	/**
	 * Check if input user is different than customer care.
	 */
	isCustomerCareOtherUser(usrId: string): Observable<boolean> {
		return this.isCustomerCare().pipe(
			map(isCC => isCC && this._currentUser$.value?.id != usrId)
		);
	}

	/**
	 * Check if user MyHexPlan product.
	 */
	isMyHexPlan(): Observable<boolean> {
		return this.getCurrentUser().pipe(
			map(usr => usr.products?.some(p => p.type == ProductTypeEnum.MyHexPlan))
		);
	}

	private _getUserProfile(): Observable<UserProfile | undefined> {
		return this.userRepo.getUserProfile().pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get user last signin.
	 */
	getLastSignIn(): Observable<UserLastSignIn> {
		return this.getCurrentUser().pipe(
			switchMap(user => this.userRepo.getLastSignIn(user.id)),
			map(res => this._handleApiResponse(res)),
			first((signin): signin is UserLastSignIn => !!signin)
		);
	}

	/**
	 * Get user languages.
	 */
	getLanguages(): Observable<string[]> {
		return this.userRepo.getLanguages().pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Change user language.
	 */
	changeLanguage(lang: string): Observable<void> {
		return this.userRepo.changeLanguage(lang).pipe(
			map(res => this._handleApiResponse(res))
		);
	}

	/**
	 * Get application URL.
	 */
	getApplicationUrl(): Observable<string> {
		return this.userRepo.getApplicationUrl().pipe(
			map(res => this._handleApiResponse(res))
		);
	}
}
